import React, { forwardRef, useEffect, useState } from "react";
import clsx from "clsx";

interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  floatingLabel?: boolean;
  containerClassName?: string;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      iconLeft,
      iconRight,
      floatingLabel = false,
      className,
      containerClassName,
      onFocus,
      onBlur,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const [inputValue, setInputValue] = useState(value || "");

    // Update inputValue when value prop changes
    useEffect(() => {
      setInputValue(value || "");
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocus(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocus(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
      onChange?.(e);
    };

    return (
      <div className={clsx("relative w-full", containerClassName)}>
        {label && !floatingLabel && (
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {label}
          </label>
        )}
        {label && floatingLabel && (
          <label
            className={clsx(
              "absolute left-3 px-1 text-gray-500 text-sm transition-all",
              focus || inputValue ? "-top-3.5 text-gray-600 text-xs" : "top-2"
            )}
          >
            {label}
          </label>
        )}
        <div className="relative flex items-start w-full mt-2">
          {iconLeft && (
            <span className="absolute left-3 text-gray-600">{iconLeft}</span>
          )}

          <textarea
            {...props}
            ref={ref}
            rows={4}
            value={inputValue}
            onFocus={(e) => {
              handleFocus(e);
            }}
            onBlur={(e) => {
              handleBlur(e);
            }}
            onChange={handleChange}
            placeholder={
              floatingLabel && (focus || inputValue) ? "" : props.placeholder
            }
            className={clsx(
              "w-full h-30 px-3 py-2 border rounded-md placeholder:text-sm transition focus:outline-none focus:ring-2",
              iconLeft && "pl-10",
              iconRight && "pr-10",
              props.disabled ? "bg-gray-200 cursor-not-allowed" : "",
              focus
                ? "focus:border-primary-500 focus:ring-primary-300"
                : "border-gray-300",
              className
            )}
          />

          {iconRight && (
            <span className="absolute right-3 text-gray-500">{iconRight}</span>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = "TextArea";

export default TextArea;
